/**
 * Project crew sign-off link DTOs (epic #1162).
 *
 * Mirrors Prisma `ProjectCrewLink` — ONE live per-project link a builder
 * shares with the whole crew. Workers open `/crew/[token]`, pick their name
 * from the project roster and land in their personal stepped sign-off flow.
 * This replaced the retired per-document sign-request links (the mobile
 * POST /documents/:id/sign-request returns 410 SIGN_REQUEST_RETIRED).
 *
 * All-plans: the crew link is the core worker sign-off entry point, so it is
 * NOT Business-gated (only Site Access *features* are). It gates on the
 * global worker-signoff kill switch and OWNER/ADMIN project management.
 */
import { z } from "zod";

export const CrewLinkSchema = z.object({
  id: z.string(),
  token: z.string(),
  /** Full public URL — `${BASE}/crew/${token}`. */
  url: z.string(),
  /** How many times workers have claimed a roster identity via this link. */
  claimCount: z.number(),
  lastClaimedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type CrewLink = z.infer<typeof CrewLinkSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const CrewLinkStrict = CrewLinkSchema.strict();

/** GET/POST response — GET returns null when no live link exists. */
export const CrewLinkResponseSchema = z
  .object({ crewLink: CrewLinkSchema.nullable() })
  .strict();
export type CrewLinkResponse = z.infer<typeof CrewLinkResponseSchema>;

/** POST body — optional expiry window; omitted = never expires. */
export const CrewLinkIssueRequest = z
  .object({
    expiresInDays: z.number().int().positive().max(365).optional(),
  })
  .strict();
export type CrewLinkIssueRequest = z.infer<typeof CrewLinkIssueRequest>;

/** DELETE response — how many active links were revoked (0 or 1). */
export const CrewLinkRevokeResponseSchema = z
  .object({ revoked: z.number() })
  .strict();
export type CrewLinkRevokeResponse = z.infer<
  typeof CrewLinkRevokeResponseSchema
>;
